(ns leg.vt
  "Moving from one state of the window to another, drawn as a move.

  A reader is pages replacing pages, and that is the one thing in here
  worth animating: which article is open, which pane a phone is showing.
  The browser can do it itself - `document.startViewTransition` takes the
  page as it is, runs the change, takes it again and cross-fades or slides
  between the two pictures. No library, no wrapper component, no element
  kept mounted to animate out of; the parts that move are named in
  `index.html` and the animation is CSS.

  Three things make it safe to call from anywhere:

  - **Not everywhere has it.** Safari got it late and some of the phones
    this is read on will never get it. Without it the change simply
    happens, which is what happens today.
  - **Not everybody wants it.** `prefers-reduced-motion: reduce` is a
    setting somebody made because motion makes them ill, and it is checked
    here rather than left to a media query - the cheapest animation is the
    one that never starts. The CSS guards it a second time.
  - **React is not synchronous.** `startViewTransition` takes its second
    picture the moment the callback's work is done, and a `swap!` on an
    atom only schedules a render. `flushSync` is what makes the DOM
    actually be the new one by then; without it both pictures are the old
    page and nothing appears to happen.

  The direction is an attribute on `<html>` rather than an argument to the
  CSS, because CSS is where it is read: forward slides left, back slides
  right, and no direction is a cross-fade.

  So is the scope, and it matters more. A view transition takes a picture of
  everything with a `view-transition-name`, so on a wide screen a change of
  article used to cross-fade all three columns and the page behind them -
  the feeds and the list had not changed at all, and fading a thing into an
  identical copy of itself is not a move, it is a flicker. The scope says
  what the change was about (`:read`, `:list`, `:all`), the CSS takes the
  name off everything else, and what has no name is not captured: it goes on
  rendering, untouched, while the one column that changed moves.

  And where the page is the whole screen, the move is not a hint but the
  page: a finger dragged sideways takes the article with it, and letting go
  keeps it going until the next article is there to take over (`drag!`,
  `glide!`, `settle!`). That is the one thing in this window that moves
  outside a view transition, and it is here rather than in the component
  because it is the same movement: what the finger starts, the transition
  carries out.

  Which is why the finger's half has to show what the transition's half
  shows - two pages edge to edge and no gap between them. The page next
  door is drawn inside the box the drag moves (`leg.article/Peek`), so it
  travels with it, and it is left there for the transition as well: the
  column is what gets captured, the peek is inside it, and the picture the
  browser takes is the only place it is drawn from then on. Taking it out
  first sounds right and costs a frame - see `index.html`.

  Which is also why nothing here measures where the page got to and hands
  the number to the CSS. It used to, and the number was always a frame or
  two stale - the glide is a transition the compositor runs, and the main
  thread's idea of where it is lags behind what is on the screen. The
  picture the browser takes does not lag: it is the page as it was drawn,
  at an offset, and the transition starts from exactly there because the
  thing it starts from *is* that picture. All the CSS has to do is let it
  (`index.html`: the group is what carries the offset, the keyframes only
  carry the page-turn)."
  (:require ["react-dom" :as react-dom]
            [leg.mui :as mui]))

(def ^:private move-attribute "data-leser-move")
(def ^:private scope-attribute "data-leser-scope")

(defn- reduced-motion? []
  (boolean (and (exists? js/matchMedia)
                (.-matches (js/matchMedia "(prefers-reduced-motion: reduce)")))))

(defn- available? []
  (and (exists? js/document)
       (some? (.-startViewTransition js/document))
       (not (reduced-motion?))))

(def ^:private page-selector
  "The page that moves when one article replaces another - the same element
  the transition captures under `leser-read`, so the finger and the slide
  move the same thing. The article and the two pages beside it and nothing
  else: the bar at the foot is outside it and stands still while the pages
  go past (`leg.article/ArticlePane`). Found in the document rather than
  held in a ref: what moves it is never the component that drew it (see
  `leg.state/read-box`, which does the same for the scrolling box inside)."
  ".leser-vt-read")

(def ^:private glide-seconds
  "How long the page goes on moving by itself once the finger is gone. The
  next article is being fetched in that time, and a page that stopped dead
  on release and jumped again when the answer came would be two movements
  where there was one gesture."
  0.3)

(def ^:private settle-seconds
  "How long it takes to come back when nothing came of the drag."
  0.2)

(defn- page []
  (when (exists? js/document)
    (.querySelector js/document page-selector)))

(defn- draggable?
  "Whether a finger may push the page sideways at all: the browser can draw
  the move, and the page is the whole screen. On a wide one the article is
  a column with two others beside it, and a column that slid out of its own
  place would be drawn over its neighbours - which is why the CSS keeps the
  wide screen to a cross-fade in place, and why nothing here moves a page
  that is not a screen."
  []
  (and (available?) (mui/one-pane?)))

(defn- offset
  "How far sideways the page is at this instant, in pixels. Read off the
  computed matrix and not out of an atom, because a glide that is already
  running is the one thing a new one has to start from."
  [^js el]
  (let [t (some-> el js/getComputedStyle .-transform)]
    (if (or (nil? t) (= "none" t) (not (exists? js/DOMMatrixReadOnly)))
      0
      (.-m41 (js/DOMMatrixReadOnly. t)))))

(defonce ^:private !move
  ;; Counted rather than flagged: `settle!` clears the inline style when its
  ;; own animation is over, and a drag that started in the meantime must not
  ;; have the style pulled out from under it.
  (atom 0))

(defn- put! [^js el transition transform]
  (swap! !move inc)
  (set! (.. el -style -transition) transition)
  (set! (.. el -style -transform) transform))

(defonce ^:private !glide
  ;; The frame the glide has asked for, so a new gesture or a transition can
  ;; take the page back off it.
  (atom nil))

(defn- stop-glide! []
  (when-let [id @!glide]
    (js/cancelAnimationFrame id)
    (reset! !glide nil)))

(defn drag!
  "The page, `dx` pixels sideways, now. Inline and with no transition of its
  own: this is called once per `touchmove` and the one thing it must do is
  be where the finger is."
  [dx]
  (when (draggable?)
    (when-let [el (page)]
      (stop-glide!)
      (put! el "none" (str "translateX(" dx "px)")))))

(defn glide!
  "Keep going. `direction` is `:forward` (out to the left) or `:back`, and
  the target is the edge of the screen - the transition below usually takes
  over long before it gets there, from wherever it got to.

  A frame at a time, and not a CSS transition, which is what this was and
  looked better in every way but one. A transition on `transform` is run by
  the compositor, which goes on drawing frames whether or not the main
  thread is keeping up - so the page on the screen is a frame or two ahead
  of the page in the document. That costs nothing until somebody takes a
  picture of the document, which is exactly what happens here when the next
  article arrives: the picture was of the page two frames back, and the
  slide that followed it began by putting the page back where it had been.
  Which is a jump, and the whole of what the swipe was supposed not to do.

  Moved by hand, the page in the document and the page on the screen are
  the same page, and the picture is of the frame that was drawn last."
  [direction]
  (when (draggable?)
    (when-let [el (page)]
      (stop-glide!)
      (let [x0 (offset el)
            x1 (cond-> (.-innerWidth js/window) (not= :back direction) -)
            t0 (js/performance.now)
            ms (* 1000 glide-seconds)
            step (fn step [now]
                   (let [p (min 1 (/ (- now t0) ms))
                         ;; Out of the hand and slowing, the same shape the
                         ;; CSS `ease-out` had.
                         e (- 1 (* (- 1 p) (- 1 p)))]
                     (put! el "none" (str "translateX(" (+ x0 (* (- x1 x0) e)) "px)"))
                     (reset! !glide (when (< p 1) (js/requestAnimationFrame step)))))]
        (reset! !glide (js/requestAnimationFrame step))))))

(defn settle!
  "Nothing came of it: the page goes back where it was. A drag that was too
  short to be meant, a finger that went off the edge, an end of the list
  with nothing beyond it, a fetch that failed.

  Does nothing where nothing moved, so the buttons and the keys can call it
  as freely as the finger can."
  []
  (when-let [el (page)]
    (stop-glide!)
    (when-not (= "" (.. el -style -transform))
      (put! el (str "transform " settle-seconds "s ease-out") "translateX(0px)")
      (let [n @!move]
        ;; And the inline style off again once it is back, so the page is a
        ;; page and not a page with a transform on it - a transformed
        ;; element is a containing block of its own and nothing else here
        ;; asked for one.
        (js/setTimeout #(when (= n @!move) (put! el "" ""))
                       (* 1000 settle-seconds))))))

(defonce ^:private !pass (atom 0))

(defn- release!
  "The inline transform off, and not a moment earlier.

  It used to come off before the browser took its picture of the old page,
  so that the picture was of a page at rest and a keyframe could carry it
  back out from where the finger had left it. Which meant the browser drew
  one frame of a page at rest - the article that had just been pushed off
  the screen, back in the middle of it - and one frame is enough to be
  seen. What was seen was the swiped-away page flashing up again.

  So it comes off here instead: after the picture has been taken and the
  next article is in the document, with the two pictures covering the
  screen for the next quarter second. Nothing is drawn from the document in
  that time, so this is the one moment at which the page can be put back
  without anybody watching.

  And it has to be put back: a transformed element is a containing block of
  its own and nothing else here asked for one, and the article that arrives
  is to be where an article is."
  []
  (stop-glide!)
  (when-let [el (page)] (put! el "" "")))

(defn transition!
  "Run `f`, and draw the difference it makes as a move.

  `direction` is `:forward`, `:back` or nil. `scope` is what the change was
  about and therefore what may move: `:read` the article alone, `:list` the
  article and the list it came out of, `:all` the whole window. Everything
  about this is optional: where the browser cannot do it or the person asked
  for no motion, `f` is called and that is the whole of it."
  ([f] (transition! nil :all f))
  ([direction f] (transition! direction :all f))
  ([direction scope f]
   (if-not (available?)
     ;; Nothing is going to be drawn, so the page the finger left behind has
     ;; to be put back by hand or the next article arrives sideways.
     (do (release!) (f))
     (let [root (.-documentElement js/document)
             ;; Which pass this is. Somebody stepping quickly starts the
             ;; next transition before this one is over, and an interrupted
             ;; transition still finishes - so the one that cleans up is the
             ;; last one to have started and not the first one to be done,
             ;; or the second slide would lose its direction halfway and
             ;; turn into a cross-fade.
             n (swap! !pass inc)
           clear! (fn [_]
                    (when (= n @!pass)
                      (.removeAttribute root move-attribute)
                      (.removeAttribute root scope-attribute)))]
       ;; Both before the first picture is taken: the scope is read as
       ;; `view-transition-name`, which decides what gets captured at all,
       ;; and a scope set afterwards would be a scope set too late.
       (if direction
         (.setAttribute root move-attribute (name direction))
         (.removeAttribute root move-attribute))
       (.setAttribute root scope-attribute (name (or scope :all)))
       (let [t (.startViewTransition js/document
                                     #(do (react-dom/flushSync f)
                                          (release!)
                                          nil))]
         ;; `finished` and not `ready`: a transition that was interrupted
         ;; by the next one still finishes, and the attributes have to come
         ;; off either way or the following cross-fade would slide.
         (-> (.-finished t) (.then clear!) (.catch clear!)))))))
